import { FormattedMessage } from 'react-intl';
import { Toggle, Dropdown } from '@ets/design-system';
import { ChangeEvent, ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { completeAction, setPulseData } from 'App/redux/onboardingSlice';
import { useDispatch, useSelector } from '_common/hooks';
import { InteractionController } from '_common/components';
import { useGetTenantSettingsQuery } from '_common/services/api/authority';
import { useOnboardingExplorerActive } from '_common/hooks/Onboarding';

type NewElementDropdownProps = {
  disabled: boolean;
  openNewFolderModal: () => void;
  openNewDocumentModal: () => void;
  handleImportContentFromFile: ChangeEventHandler<HTMLInputElement>;
  handleFileUploadChange: ChangeEventHandler<HTMLInputElement>;
  handleImportFile: (e: ChangeEvent<HTMLInputElement>, type: 'dopdf' | 'presentation') => void;
};

const NewElementDropdown = ({
  disabled,
  openNewFolderModal,
  openNewDocumentModal,
  handleImportContentFromFile,
  handleFileUploadChange,
  handleImportFile,
}: NewElementDropdownProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const importDocumentOptionRef = useRef<HTMLDivElement>(null);
  const actionsCompleted = useSelector((state) => state.onboarding.actionsCompleted);
  const onboardingExplorerIsActive = useOnboardingExplorerActive();
  const interaction = useSelector((state) => state.onboarding.interactions);
  const [mouseOn, setMouseOn] = useState(false);
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  useEffect(() => {
    if (isOpen && onboardingExplorerIsActive) {
      dispatch(completeAction('explorer_myFiles_new_open'));
    }
  }, [isOpen, onboardingExplorerIsActive]);

  useEffect(() => {
    if (!mouseOn && actionsCompleted.explorer_myFiles_new_open) {
      if (dropdownRef?.current) {
        dispatch(
          setPulseData({
            documentOptionRect: {
              top: dropdownRef.current.offsetTop,
              left: dropdownRef.current.offsetLeft,
              height: dropdownRef.current.offsetHeight,
              width: dropdownRef.current.offsetWidth,
            },
            importDocumentOptionRect: undefined,
          }),
        );
      } else {
        dispatch(
          setPulseData({
            documentOptionRect: undefined,
            importDocumentOptionRect: undefined,
          }),
        );
      }
    } else {
      if (
        importDocumentOptionRef?.current &&
        Object.prototype.hasOwnProperty.call(interaction, 'explorer_document_option')
      ) {
        dispatch(
          setPulseData({
            importDocumentOptionRect: {
              top: importDocumentOptionRef.current.offsetTop,
              left: importDocumentOptionRef.current.offsetLeft,
              height: importDocumentOptionRef.current.offsetHeight,
              width: importDocumentOptionRef.current.offsetWidth,
            },
            documentOptionRect: undefined,
          }),
        );
      } else {
        dispatch(
          setPulseData({
            documentOptionRect: undefined,
            importDocumentOptionRect: undefined,
          }),
        );
      }
    }

    return () => {
      dispatch(
        setPulseData({
          documentOptionRect: undefined,
        }),
      );
    };
  }, [actionsCompleted, mouseOn]);
  return (
    <Dropdown.Root
      open={
        onboardingExplorerIsActive &&
        actionsCompleted?.explorer_myFiles_new_open &&
        !actionsCompleted?.explorer_my_files_document_option_click
          ? true
          : isOpen
      }
      onOpenChange={() => {
        if (!disabled) setIsOpen(!isOpen);
      }}
    >
      <Dropdown.Trigger>
        <InteractionController
          environment="explorer"
          rules={[
            {
              interaction: 'explorer_my_files_action_bar_new',
              actions: ['explorer_my_files_document_option_click'],
            },
          ]}
        >
          <Toggle
            variant="standard"
            iconProps={{
              icon: 'circle-plus',
              color: !isOpen ? 'primary' : undefined,
            }}
            isToggled={isOpen}
            testId="new-dropdown"
            disabled={disabled}
          >
            <FormattedMessage id="storage.actionBar.new.new" />
          </Toggle>
        </InteractionController>
      </Dropdown.Trigger>
      <div ref={dropdownRef}>
        <Dropdown.Content testId="new-dropdown">
          <InteractionController
            environment="explorer"
            style={{ width: '100%', flexDirection: 'column' }}
          >
            <Dropdown.Item
              prefix="folder"
              onClick={openNewFolderModal}
              disabled={disabled}
              testId="new-folder-dropdown-item"
            >
              <FormattedMessage id="storage.actionBar.filters.folder" />
              ...
            </Dropdown.Item>
          </InteractionController>
          <div
            ref={importDocumentOptionRef}
            onMouseOver={() => {
              if (
                onboardingExplorerIsActive &&
                Object.prototype.hasOwnProperty.call(interaction, 'explorer_document_option')
              ) {
                setMouseOn(true);
                document.body.style.pointerEvents = 'auto';
              }
            }}
            onMouseLeave={() => {
              if (onboardingExplorerIsActive) {
                setMouseOn(false);
              }
            }}
          >
            <Dropdown.Sub>
              <Dropdown.SubTrigger prefix="file-lines" testId="new-document-dropdown-submenu">
                <FormattedMessage id="global.document" />
                ...
              </Dropdown.SubTrigger>
              <Dropdown.SubContent testId="new-document-dropdown-subcontent-menu">
                <InteractionController
                  environment="explorer"
                  style={{ width: '100%', flexDirection: 'column' }}
                  rules={[{ interaction: 'explorer_document_option' }]}
                >
                  <Dropdown.Item
                    onClick={() => {
                      openNewDocumentModal();
                      dispatch(completeAction('explorer_my_files_document_option_click'));
                    }}
                    testId="blank-document-dropdown-item"
                  >
                    <FormattedMessage id="storage.actionBar.new.blankDocument" />
                  </Dropdown.Item>
                </InteractionController>
                <InteractionController
                  environment="explorer"
                  style={{ width: '100%', flexDirection: 'column' }}
                >
                  <Dropdown.Upload
                    onChange={handleImportContentFromFile}
                    accept=".docx, .html, .htm"
                    testId="import-document-dropdown-item"
                  >
                    <FormattedMessage id="storage.actionBar.new.importDocument" />
                  </Dropdown.Upload>
                </InteractionController>
              </Dropdown.SubContent>
            </Dropdown.Sub>
          </div>
          <InteractionController
            environment="explorer"
            style={{ width: '100%', flexDirection: 'column' }}
          >
            {tenantSettings?.['product.pdf_reviewer'] && (
              <Dropdown.Upload
                onChange={(e) => handleImportFile(e, 'dopdf')}
                accept=".pdf"
                prefix="file-pdf"
                testId="import-pdf-document-dropdown-item"
              >
                <FormattedMessage id="PDF_IMPORT" />…
              </Dropdown.Upload>
            )}
            {tenantSettings?.['product.presentation_reviewer'] && (
              <Dropdown.Upload
                onChange={(e) => handleImportFile(e, 'presentation')}
                accept=".pptx"
                prefix="file-ppt"
                testId="import-pptx-document-dropdown-item"
              >
                <FormattedMessage id="PRESENTATION_IMPORT" />…
              </Dropdown.Upload>
            )}
            <Dropdown.Upload
              prefix="file"
              onChange={handleFileUploadChange}
              multiple
              accept=".rtf, .html, .xhtml, .css, .pdf, .ppt, .pptx, .xls,
        .xlsx, .csv, .txt, .jpg, .jpeg, .png, .bmp, .tiff, .gif, .svg, .dwg,
        .dxf, .dgn, .stl, .mkv, .mov, .avi, .mp4, .m4v, .ogv, .webm, .qt, .mpg,
        .wmv, .asf, .flv, .aac, .flac, .wav, .mp3, .zip, .rar, .cls, .sty, .bst,
        .bib, .tex, .aux, .log, .docx, .dtd, .json, .kml, .mol, .cml, .xml, .xsl, 
        .xpt, .xport, .sdf, .xsd"
              testId="new-file-dropdown-item"
            >
              <FormattedMessage id="FILE_IMPORT" />
            </Dropdown.Upload>
          </InteractionController>
        </Dropdown.Content>
      </div>
    </Dropdown.Root>
  );
};

export default NewElementDropdown;
